import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TextField from '@/components/common/TextField'
import RecoveryPhrase from '@/components/RecoveryPhrase'
import RecoveryPhraseInput from '@/components/RecoveryPhraseInput'
import { Info, Shield, CheckCircle, XCircle } from 'lucide-react'
import { generateMnemonic } from '@/utils/wallet'
import {
  calculatePasswordStrength,
  validatePasswordMatch
} from '@/utils/passwordStrength'
import walletService from '@/services/wallet/wallet.service'
import Snackbar from '@/components/Snackbar'
import LanguageSelector from '@/components/common/LanguageSelector'

const Registration = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [mnemonic, setMnemonic] = useState('')
  const [isMnemonicValid, setIsMnemonicValid] = useState(false)
  const [snackbarState, setSnackbarState] = useState<{
    isOpen: boolean
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    message: '',
    type: 'info'
  })

  // Maps raw errors to user-friendly i18n keys
  const getFriendlyError = (error: unknown): string => {
    const msg = error instanceof Error ? error.message : String(error)
    if (
      msg.includes('incorrect password') ||
      msg.includes('INVALID_ARGUMENT')
    ) {
      return t('dialogs.wrong_password')
    }
    if (
      msg.includes('invalid mnemonic') ||
      msg.includes('invalid entropy') ||
      msg.includes('Invalid mnemonic')
    ) {
      return t('dialogs.invalid_mnemonic')
    }
    return t('dialogs.verify_error')
  }

  // Ya desbloqueado: para agregar otra wallet existe /add-wallet (misma contraseña).
  useEffect(() => {
    if (walletService.isWalletUnlocked()) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  // Listen for back button clicks from header
  useEffect(() => {
    const handleBackEvent = () => {
      handleBackStep()
    }

    window.addEventListener('registration-back', handleBackEvent)
    return () => {
      window.removeEventListener('registration-back', handleBackEvent)
    }
  }, [currentStep]) // Re-attach when currentStep changes

  // Calculate password strength
  const passwordStrength = useMemo(
    () => calculatePasswordStrength(password),
    [password]
  )

  // Validate password match
  const passwordMatch = useMemo(
    () => validatePasswordMatch(password, confirmPassword),
    [password, confirmPassword]
  )

  // Check if step 1 is valid
  const isStep1Valid =
    password.length >= 8 && passwordStrength.score >= 2 && passwordMatch.isValid

  const handleBackStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    } else {
      // If on step 1, go back to previous page
      navigate(-1)
    }
  }

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (!isStep1Valid) return
      // Generate mnemonic when moving to step 2
      const generatedMnemonic = generateMnemonic()
      setMnemonic(generatedMnemonic)
      setCurrentStep(2)
      return
    }

    if (currentStep === 2) {
      setCurrentStep(3)
      return
    }

    if (currentStep === 3) {
      // Final step - create wallet and redirect to dashboard
      if (!isMnemonicValid) return

      try {
        await walletService.importFromMnemonic(
          mnemonic,
          password,
          'Primary Wallet'
        )
        console.log('Wallet created successfully')
        // Navigate to dashboard
        navigate('/dashboard', { replace: true })
      } catch (error) {
        console.error('Error creating wallet:', error)
        setSnackbarState({
          isOpen: true,
          message: getFriendlyError(error),
          type: 'error'
        })
      }

      return
    }
  }

  const handleMnemonicValidation = useCallback(
    (isValid: boolean, userMnemonic: string) => {
      setIsMnemonicValid(isValid)
      console.log('Mnemonic validation:', { isValid, userMnemonic })
    },
    []
  )

  // Get the number of filled bars based on password strength
  const getFilledBars = () => {
    if (!password) return 0
    return passwordStrength.score + 1 // 0-4 becomes 1-5, but we show max 4
  }

  return (
    <div className="flex flex-col px-6 pt-4 relative min-h-[calc(100vh-7rem)] lg:min-h-0 lg:max-h-205 lg:max-w-213 lg:place-self-center">
      <div className="flex flex-col gap-2 mb-6">
        <p className="uppercase font-medium tracking-widest secondary-gradient-text">
          {t('registration.step', { current: currentStep, total: 3 })}
        </p>
        {/* STEPPER */}
        <div className="flex gap-4">
          <div
            onClick={() => setCurrentStep(1)}
            className={`w-1/3 h-1 rounded-full cursor-pointer ${currentStep === 1 ? 'secondary-gradient' : 'bg-card'}`}
          />
          <div
            onClick={() => isStep1Valid && setCurrentStep(2)}
            className={`w-1/3 h-1 rounded-full ${currentStep === 2 ? 'secondary-gradient' : 'bg-card'} ${isStep1Valid ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
          />
          <div
            onClick={() => isStep1Valid && setCurrentStep(3)}
            className={`w-1/3 h-1 rounded-full ${currentStep === 3 ? 'secondary-gradient' : 'bg-card'} ${isStep1Valid ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
          />
        </div>
      </div>

      {/* PASO 1 */}
      {currentStep === 1 && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-6 lg:gap-3">
            <h1 className="font-extrabold text-4xl lg:text-2xl lg:font-semibold tracking-tighter text-text-main">
              {t('registration.step1_title')}
            </h1>
            <p className="text-text-secondary leading-7">
              {t('registration.step1_desc')}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-lg text-text-main">
              {t('registration.new_password')}
            </label>
            <TextField
              type="password"
              placeholder={t('registration.password_placeholder')}
              showPasswordToggle
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <label className="font-semibold text-lg text-text-main mt-4">
              {t('registration.confirm_password')}
            </label>
            <TextField
              type="password"
              placeholder={t('registration.confirm_placeholder')}
              showPasswordToggle
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            {/* Password match indicator */}
            {confirmPassword && (
              <div
                className={`flex items-center gap-2 mt-2 text-sm ${
                  passwordMatch.isValid ? 'text-success' : 'text-red-500'
                }`}
              >
                {passwordMatch.isValid ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>{t(passwordMatch.message)}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    <span>{t(passwordMatch.message)}</span>
                  </>
                )}
              </div>
            )}

            {/* Password strength indicator */}
            <div className="mt-4 flex flex-col bg-card rounded-xl p-5 gap-4">
              <div className="flex justify-between">
                <p className="font-medium text-sm text-text-secondary">
                  {t('registration.password_strength_label')}
                </p>
                <p className={`font-bold text-sm ${passwordStrength.color}`}>
                  {t(passwordStrength.label)}
                </p>
              </div>
              <div className="flex gap-2">
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={`w-1/4 h-2 rounded-full transition-all ${
                      index < getFilledBars()
                        ? passwordStrength.score <= 1
                          ? 'bg-red-500'
                          : passwordStrength.score === 2
                            ? 'bg-yellow-500'
                            : 'bg-success'
                        : 'bg-card-e2'
                    }`}
                  />
                ))}
              </div>
              {passwordStrength.suggestions.length > 0 && (
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-text-secondary/80 shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1">
                    {passwordStrength.suggestions.map((suggestion, index) => (
                      <p key={index} className="text-text-secondary/80 text-xs">
                        {t(suggestion)}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PASO 2 */}
      {currentStep === 2 && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-6 lg:gap-3">
            <h1 className="font-extrabold text-4xl lg:text-2xl lg:font-semibold tracking-tighter text-text-main">
              {t('registration.step2_title')}
            </h1>
            <p className="text-text-secondary leading-7">
              {t('registration.step2_desc')}
            </p>
          </div>
          {/*           <div className="bg-card rounded-xl flex p-5 gap-4">
            <div className="h-9 w-9 min-w-9 bg-card-e2 rounded-lg justify-items-center content-center">
              <Shield className="h-4 w-5 icon-gradient" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-bold text-text-main">
                {t('registration.tip_title')}
              </p>
              <p className="text-text-secondary text-sm leading-5">
                {t('registration.tip_desc')}
              </p>
            </div>
          </div> */}

          {/* Recovery Phrase Component */}
          <RecoveryPhrase mnemonic={mnemonic} isRevealed={false} />
        </div>
      )}

      {/* PASO 3 */}
      {currentStep === 3 && (
        <div className="flex flex-col gap-6">
          <h1 className="font-extrabold text-4xl lg:text-2xl lg:font-semibold tracking-tighter text-text-main">
            {t('registration.step3_title')}
          </h1>

          {/* Recovery Phrase Input Component */}
          <RecoveryPhraseInput
            expectedMnemonic={mnemonic}
            onValidationChange={handleMnemonicValidation}
            showValidation={true}
          />
        </div>
      )}

      <div className="mt-auto lg:mt-6 pt-6">
        <div className="flex flex-col gap-4">
          <button
            onClick={handleNextStep}
            disabled={
              (currentStep === 1 && !isStep1Valid) ||
              (currentStep === 3 && !isMnemonicValid)
            }
            className={`w-full btn-gradient-fill-secondary rounded-full items-center py-4 ${currentStep !== 1 ? 'mb-8' : ''} disabled:opacity-50 disabled:hover:opacity-50 disabled:cursor-not-allowed transition-opacity`}
          >
            <p className="font-extrabold text-xl">
              {currentStep === 3
                ? t('registration.create_wallet')
                : t('registration.next_step')}
            </p>
          </button>
        </div>
      </div>
      <Snackbar
        message={snackbarState.message}
        isOpen={snackbarState.isOpen}
        type={snackbarState.type}
        onClose={() => setSnackbarState((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Registration
